/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// levels.js — the signal-level vocabulary and ternary gate primitives shared
// by the whole engine. Pure and DOM-free.
//
//   H  logic high        L  logic low
//   Z  high impedance / undriven (a floating node)
//   X  conflict / unknown
//
// TTL authenticity: a FLOATING TTL input reads HIGH — `asInput(Z) === H` — a
// deliberate, documented choice (real 74xx inputs pull themselves high). A
// CMOS input has nothing to pull it anywhere, so a CD4000 part reads its
// floating inputs through `asCmosInput` instead (Z → X; Feature 400). `X`
// propagates as `X` except where a dominant input forces the result (a NAND
// with any `L` is `H` regardless of an `X` on another pin — the standard
// ternary-logic shortcut).
//
// The primitives operate on driven levels (H/L/X); callers `asInput()` a pin
// first so `Z` never reaches a gate. Tri-state `buf3` may RETURN `Z`.

export const H = "H";
export const L = "L";
export const Z = "Z";
export const X = "X";

/** A DRIVER's level (never a net's): an open-collector output that may sink
    or may let go — its gate's level unknown. A LOW beside it is LOW (a
    wired-AND), anything else is X (resolve.js). Driver lists only; no gate
    ever reads it. */
export const OC_X = "oc?";

/**
 * Read a pin as a gate input: a floating pin (`Z`) pulls HIGH; H/L/X pass
 * through unchanged. This is the one place the "floating reads high" rule
 * lives.
 */
export function asInput(level) {
  return level === Z ? H : level;
}

/**
 * Read a pin as a CMOS gate input: a floating pin (`Z`) is UNKNOWN — a gate
 * with nothing on it reads whatever the air puts there — so it becomes `X`;
 * H/L/X pass through. The CMOS twin of `asInput`, chosen per part by its
 * family (catalog/families.js `floatsUnknown`).
 */
export function asCmosInput(level) {
  return level === Z ? X : level;
}

const some = (arr, v) => arr.some((x) => x === v);

/** AND: any L → L; else any X → X; else H. */
export function and(...ins) {
  if (some(ins, L)) return L;
  if (some(ins, X)) return X;
  return H;
}

/** OR: any H → H; else any X → X; else L. */
export function or(...ins) {
  if (some(ins, H)) return H;
  if (some(ins, X)) return X;
  return L;
}

/** NAND: any L → H (dominant); else any X → X; else L. */
export function nand(...ins) {
  if (some(ins, L)) return H;
  if (some(ins, X)) return X;
  return L;
}

/** NOR: any H → L (dominant); else any X → X; else H. */
export function nor(...ins) {
  if (some(ins, H)) return L;
  if (some(ins, X)) return X;
  return H;
}

/** XOR: no dominant value — any X → X; else odd count of H → H. */
export function xor(...ins) {
  if (some(ins, X)) return X;
  return ins.filter((x) => x === H).length % 2 === 1 ? H : L;
}

/** XNOR: the complement of XOR — any X → X; else even count of H → H. */
export function xnor(...ins) {
  return inv(xor(...ins));
}

/** BUF: a non-inverting buffer — H/L pass through, X stays X. */
export function buf(a) {
  return a === H || a === L ? a : X;
}

/** INV: H↔L; X → X (Z is asInput'd to H before it reaches here). */
export function inv(a) {
  if (a === H) return L;
  if (a === L) return H;
  return X;
}

/**
 * Tri-state buffer with ACTIVE-LOW enable: drives `data` while `enable` is
 * L, floats (`Z`) while `enable` is H, and is unknown (`X`) when the enable
 * itself is unknown.
 */
export function buf3(data, enable) {
  if (enable === L) return data;
  if (enable === H) return Z;
  return X;
}

/**
 * `fn` over every clean reading of `levels` (H/L/X), each X tried both ways:
 * the level every reading agrees on, or X where they differ. So an unknown
 * that cannot change the answer does not spoil it — a 4028 with D floating
 * still holds outputs 2–7 LOW, since neither reading of D selects them.
 * @param {string[]} levels
 * @param {(clean: string[]) => string} fn
 */
export function overUnknowns(levels, fn) {
  if (!levels.includes(X)) return fn(levels); // (the common case, no copy)
  const unknown = levels.flatMap((lv, i) => (lv === X ? [i] : []));
  let result = null;
  for (let k = 0; k < 1 << unknown.length; k++) {
    const clean = levels.slice();
    unknown.forEach((at, bit) => {
      clean[at] = (k >> bit) & 1 ? H : L;
    });
    const value = fn(clean);
    result = result === null || result === value ? value : X;
    if (result === X) return X;
  }
  return result;
}
